import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircleIcon, XCircleIcon, CalendarDaysIcon, } from '@heroicons/react/24/outline';
import { Spinner } from '@/components/Spinner';
import { useMyOrders, useCancelOrder } from '@/hooks/useApi';
import { formatJalaliWithWeekday, todayJalali } from '@/lib/jalali';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
export function MyOrdersList() {
    const { t } = useTranslation();
    const q = useMyOrders();
    const cancel = useCancelOrder();
    const { confirm, node: confirmNode } = useConfirm();
    const today = todayJalali();
    const handleCancel = async (id) => {
        if (await confirm({ message: t('orders.cancelConfirm'), danger: true })) {
            try {
                await cancel.mutateAsync(id);
                toast.success('سفارش لغو شد');
            }
            catch (e) {
                toast.error(e?.response?.data?.error || 'خطا');
            }
        }
    };
    if (q.isLoading)
        return _jsx(Spinner, {});
    // Sort future-or-today first, then past (descending).
    const all = q.data?.data ?? [];
    const future = all.filter((o) => (o.dailyMenu?.jalaliDate ?? '') >= today)
        .sort((a, b) => (a.dailyMenu?.jalaliDate ?? '').localeCompare(b.dailyMenu?.jalaliDate ?? ''));
    const past = all.filter((o) => (o.dailyMenu?.jalaliDate ?? '') < today)
        .sort((a, b) => (b.dailyMenu?.jalaliDate ?? '').localeCompare(a.dailyMenu?.jalaliDate ?? ''));
    const orders = [...future, ...past];
    if (orders.length === 0) {
        return (_jsxs("div", { className: "rounded-xl border-2 border-dashed border-gray-200 p-10 text-center text-sm text-gray-500 dark:border-gray-800", children: [_jsx(CalendarDaysIcon, { className: "mx-auto mb-2 h-10 w-10 text-gray-300" }), t('orders.noOrders')] }));
    }
    return (_jsxs(_Fragment, { children: [confirmNode, _jsx("ul", { className: "space-y-2", children: orders.map((o, i) => {
                    const jdate = o.dailyMenu?.jalaliDate ?? '';
                    const isToday = jdate === today;
                    const isPast = jdate < today;
                    const canCancel = o.status === 'SET' && !isToday && !isPast;
                    return (_jsxs(motion.li, { initial: { opacity: 0, y: 4 }, animate: { opacity: 1, y: 0 }, transition: { delay: i * 0.02 }, className: "flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-800 dark:bg-gray-900", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "flex h-10 w-10 items-center justify-center rounded-full bg-arshe-100 text-arshe-700 dark:bg-arshe-900/40 dark:text-arshe-300", children: _jsx(CalendarDaysIcon, { className: "h-5 w-5" }) }), _jsxs("div", { children: [_jsxs("div", { className: "text-sm font-medium text-gray-900 dark:text-gray-100", children: [formatJalaliWithWeekday(jdate), isToday && _jsx("span", { className: "ms-2 rounded-full bg-arshe-500 px-1.5 py-0.5 text-[10px] font-semibold text-white dark:bg-arshe-600", children: "\u0627\u0645\u0631\u0648\u0632" })] }), _jsx("div", { className: "text-xs text-gray-500", children: o.items[0]?.foodName })] })] }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx(StatusBadge, { status: o.status }), canCancel && (_jsx("button", { onClick: () => handleCancel(o.id), className: "rounded-md px-2 py-1 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-950", children: "\u0644\u063A\u0648" }))] })] }, o.id));
                }) })] }));
}
function StatusBadge({ status }) {
    const cfg = {
        SET: { cls: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200', Icon: CheckCircleIcon, label: 'ثبت شده' },
        CANCELLED: { cls: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200', Icon: XCircleIcon, label: 'لغو شده' },
    };
    const c = cfg[status] ?? cfg.SET;
    return (_jsxs("span", { className: `inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${c.cls}`, children: [_jsx(c.Icon, { className: "h-3 w-3" }), c.label] }));
}
